'use client'

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useShallow } from 'zustand/react/shallow'
import type { AssetSummary } from '@/lib/assets/types'
import { useAudioStore } from '@/lib/store/audio-store'
import { PreviewPlayerWaveform } from './preview-player-waveform'
import styles from './preview-player-bar.module.scss'

export function formatPreviewTime(value: number, audio: boolean) {
  const safe = Number.isFinite(value) ? Math.max(0, value) : 0
  if (!audio) return `${safe.toFixed(2)} s`
  const seconds = Math.floor(safe)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

type Props = {
  asset?: AssetSummary | null
  audioSrc: string | null
  isAudio: boolean
  video?: HTMLVideoElement | null
  playing: boolean
  disabled?: boolean
  duration: number
  getTime: () => number
  play: () => void
  pause: () => void
  seek: (time: number) => void
}

export function PreviewPlayerProgress({
  asset,
  audioSrc,
  isAudio,
  video,
  playing,
  disabled = false,
  duration,
  getTime,
  play,
  pause,
  seek,
}: Props) {
  const audio = useAudioStore(
    useShallow((s) => ({ src: s.currentSrc, time: s.currentTime, duration: s.duration, playing: s.isPlaying }))
  )
  const [videoTime, setVideoTime] = useState(0)
  const [dragTime, setDragTime] = useState<number | null>(null)
  const currentAudio = !!audioSrc && audio.src === audioSrc
  const total = isAudio ? (currentAudio && audio.duration ? audio.duration : duration) : duration
  const time = dragTime ?? (isAudio ? (currentAudio ? audio.time : 0) : videoTime)
  const binding = isAudio ? audioSrc : video
  const gesture = useRef<{ binding: typeof binding; resume: boolean } | null>(null)
  const latest = useRef({ binding, play, pause, seek, isAudio, audioSrc })
  latest.current = { binding, play, pause, seek, isAudio, audioSrc }

  useEffect(() => {
    if (isAudio || !video) return
    let frame = 0
    const sync = () => setVideoTime(getTime())
    const tick = () => {
      sync()
      frame = requestAnimationFrame(tick)
    }
    const events = ['seeking', 'seeked', 'loadedmetadata', 'timeupdate']
    events.forEach((event) => video.addEventListener(event, sync))
    sync()
    if (playing) frame = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(frame)
      events.forEach((event) => video.removeEventListener(event, sync))
    }
  }, [duration, getTime, isAudio, playing, video])

  useEffect(() => {
    gesture.current = null
    setDragTime(null)
    return () => {
      gesture.current = null
    }
  }, [binding])

  const start = () => {
    if (gesture.current) return
    gesture.current = { binding, resume: isAudio ? currentAudio && audio.playing : playing }
    if (isAudio) useAudioStore.getState().pause()
    else pause()
  }
  const move = (value: number) => {
    const valueClamped = Math.min(total, Math.max(0, value))
    setDragTime(valueClamped)
    if (isAudio) {
      const store = useAudioStore.getState()
      if (audioSrc && store.currentSrc !== audioSrc) {
        store.play(audioSrc, valueClamped)
        store.pause()
      } else store.seek(valueClamped)
    } else seek(valueClamped)
  }
  const finish = () => {
    const held = gesture.current
    gesture.current = null
    setDragTime(null)
    if (!held || held.binding !== latest.current.binding || !held.resume) return
    if (latest.current.isAudio) {
      if (latest.current.audioSrc) useAudioStore.getState().play(latest.current.audioSrc)
    } else latest.current.play()
  }
  const progress = total ? Math.min(100, Math.max(0, (time / total) * 100)) : 0
  const scrubKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']

  return (
    <div className={styles.timeline} data-waveform={isAudio}>
      <span>{formatPreviewTime(time, isAudio)}</span>
      <div className={styles.seekTrack}>
        {isAudio && <PreviewPlayerWaveform asset={asset} progress={progress} />}
        <input
          type="range"
          min={0}
          max={total || 1}
          step={0.001}
          value={Math.min(time, total || 1)}
          disabled={disabled || !total || (isAudio ? !audioSrc : !video)}
          data-scrubbing={dragTime !== null}
          onPointerDown={(event) => {
            if (event.button !== 0) return
            event.currentTarget.setPointerCapture(event.pointerId)
            start()
          }}
          onPointerUp={finish}
          onPointerCancel={finish}
          onLostPointerCapture={finish}
          onBlur={finish}
          onKeyDown={(event) => {
            if (scrubKeys.includes(event.key)) start()
          }}
          onKeyUp={(event) => {
            if (scrubKeys.includes(event.key)) finish()
          }}
          onChange={(event) => {
            const value = Number(event.target.value)
            if (gesture.current) move(value)
            else {
              if (isAudio) useAudioStore.getState().seek(value)
              else seek(value)
            }
          }}
          aria-label="Preview progress"
          aria-valuetext={`${formatPreviewTime(time, isAudio)} of ${formatPreviewTime(total, isAudio)}`}
          style={{ '--progress': `${progress}%` } as CSSProperties}
        />
      </div>
      <span>{formatPreviewTime(total, isAudio)}</span>
    </div>
  )
}
